/**
 * Game Data section.
 *
 * League/data-set context is section-wide. EPL is active today; WC uses the
 * same read surfaces so backend/admin users can inspect ingestion progress.
 */

import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { GameDataOverviewTab } from './GameDataOverviewTab';
import { PlayersTab } from './PlayersTab';
import { TeamsTab } from './TeamsTab';
import { CoachesTab } from './CoachesTab';
import { FixturesTab } from './FixturesTab';
import { NewsTab } from './NewsTab';
import { RulesTab } from './RulesTab';
import { DefinitionsTab } from './DefinitionsTab';
import { SimulationTab } from './SimulationTab';
import { useGameDataSources, type LeagueName } from './api';
import { DataTableShell } from './shared';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'players', label: 'Players' },
  { id: 'teams', label: 'Teams' },
  { id: 'coaches', label: 'Coaches' },
  { id: 'fixtures', label: 'Fixtures' },
  { id: 'data-sources', label: 'Data Sources' },
  { id: 'news', label: 'News' },
  { id: 'rules', label: 'Rules' },
  { id: 'definitions', label: 'Definitions' },
  { id: 'simulation', label: 'Simulation' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function ContentPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const rawView = searchParams.get('view');
  const tabParam =
    rawTab === 'sports-leagues' ||
    rawTab === 'seasons' ||
    rawTab === 'scoring-rules' ||
    rawTab === 'squad-rules' ||
    rawTab === 'teams-players'
      ? legacyTab(rawTab, rawView)
      : rawTab;
  const activeTab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : 'overview';
  const league: LeagueName = searchParams.get('league') === 'WC' ? 'WC' : 'EPL';

  const setTab = (id: TabId) => {
    // Switching tab clears any drawer/view state — they're tab-specific.
    const next = new URLSearchParams();
    next.set('tab', id);
    next.set('league', league);
    setSearchParams(next, { replace: true });
  };

  const setLeague = (nextLeague: LeagueName) => {
    const next = new URLSearchParams(searchParams);
    next.set('league', nextLeague);
    next.delete('id');
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Game Data"
        description="Sports, teams, players, fixtures, and scoring configuration."
      />
      <div className="px-8 py-6">
        <DataSetBar league={league} onChange={setLeague} />

        <div className="mb-4 flex flex-wrap items-center gap-1 border-b">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                activeTab === t.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' ? <GameDataOverviewTab league={league} /> : null}
        {activeTab === 'players' ? <PlayersTab league={league} /> : null}
        {activeTab === 'teams' ? <TeamsTab league={league} /> : null}
        {activeTab === 'coaches' ? <CoachesTab league={league} /> : null}
        {activeTab === 'fixtures' ? <FixturesTab league={league} /> : null}
        {activeTab === 'data-sources' ? <DataSourcesTab league={league} /> : null}
        {activeTab === 'news' ? <NewsTab league={league} /> : null}
        {activeTab === 'rules' ? <RulesTab league={league} /> : null}
        {activeTab === 'definitions' ? <DefinitionsTab league={league} /> : null}
        {activeTab === 'simulation' ? <SimulationTab league={league} /> : null}
      </div>
    </>
  );
}

function DataSetBar({ league, onChange }: { league: LeagueName; onChange: (league: LeagueName) => void }) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2 rounded-md border bg-card px-4 py-3">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Data set
      </span>
      <button
        type="button"
        onClick={() => onChange('EPL')}
        className={cn(
          'rounded-full border px-3 py-1 text-xs font-medium',
          league === 'EPL'
            ? 'border-primary bg-primary/10 text-foreground'
            : 'bg-muted/40 text-muted-foreground',
        )}
      >
        EPL 2026/27
      </button>
      <button
        type="button"
        onClick={() => onChange('WC')}
        className={cn(
          'rounded-full border px-3 py-1 text-xs font-medium',
          league === 'WC'
            ? 'border-primary bg-primary/10 text-foreground'
            : 'bg-muted/40 text-muted-foreground',
        )}
      >
        World Cup 2026
      </button>
      <Badge variant="outline" className="ml-auto">
        Football
      </Badge>
    </div>
  );
}

function legacyTab(tab: string, view: string | null): TabId {
  if (tab === 'scoring-rules' || tab === 'squad-rules') return 'rules';
  if (tab === 'teams-players') {
    return view === 'teams' ? 'teams' : 'players';
  }
  return 'overview';
}

function DataSourcesTab({ league }: { league: LeagueName }) {
  const { data, isLoading, error } = useGameDataSources(league);
  const rows = data?.data ?? [];

  if (isLoading || error || rows.length === 0) {
    return (
      <DataTableShell
        loading={isLoading}
        error={error}
        empty={rows.length === 0}
        emptyLabel="No data sources"
      >
        <div />
      </DataTableShell>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row.id} className="rounded-md border bg-card px-4 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-foreground">{row.source}</div>
              <div className="mt-1 text-xs text-muted-foreground">{row.detail}</div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="rounded-full border px-2 py-0.5 text-muted-foreground">
                {row.dataset}
              </span>
              <span
                className={
                  row.status === 'synced'
                    ? 'rounded-full bg-emerald-500/15 px-2 py-0.5 text-emerald-700 dark:text-emerald-300'
                    : row.status === 'configured'
                      ? 'rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-700 dark:text-amber-300'
                      : 'rounded-full bg-muted px-2 py-0.5 text-muted-foreground'
                }
              >
                {row.status.replace(/_/g, ' ')}
              </span>
              <span className="text-muted-foreground">
                {row.records.toLocaleString()} records
              </span>
            </div>
          </div>

          {row.config && row.config.length > 0 ? (
            <dl className="mt-3 grid gap-x-4 gap-y-1.5 border-t pt-3 text-xs sm:grid-cols-[140px_1fr]">
              {row.config.map((entry) => (
                <DataSourceConfigEntry key={entry.label} entry={entry} />
              ))}
            </dl>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function DataSourceConfigEntry({
  entry,
}: {
  entry: { label: string; value: string; secret?: boolean };
}) {
  return (
    <>
      <dt className="text-muted-foreground">{entry.label}</dt>
      <dd
        className={
          entry.secret
            ? 'font-mono text-foreground/80 break-all'
            : 'font-mono text-foreground break-all'
        }
      >
        {entry.value}
      </dd>
    </>
  );
}
